import type { Event } from '@kalos/kalos-rpc';
import { differenceInDays, format, parseISO } from 'date-fns';
import { useMemo } from 'react';

export interface RecentCompletedVisit {
  event: Event;
  date: Date;
  daysAgo: number;
  /** Human-readable summary, e.g. "10 days ago (May 26)" */
  label: string;
}

/**
 * Finds the property's most recent visit completed within the last 30 days so
 * the CSR can flag the new job as a callback. A job counts as "completed" if
 * its status is Completed or it carries a completion date. Shared by the Review
 * step (Step 2) and the Schedule step (Step 3) so the 30-day rule stays in one
 * place.
 */
export const useRecentCompletedVisit = (
  events: Event[] | undefined,
  enabled = true,
): RecentCompletedVisit | null => {
  return useMemo(() => {
    if (!enabled) return null;
    const now = new Date();
    // MySQL zero-date sentinels ('0000-00-00 00:00:00', NULL_TIME
    // '0001-01-01 00:00:00') are truthy strings; treat them as "no date" so the
    // fallback chain reaches a real date instead of short-circuiting on a
    // sentinel. Mirrors the existing `startsWith('000')` guard used elsewhere.
    const cleanDate = (s?: string) => {
      const t = s?.trim();
      return t && !t.startsWith('000') ? t : '';
    };
    let best: { event: Event; date: Date; daysAgo: number } | null = null;
    for (const ev of events ?? []) {
      const completedTimestamp = cleanDate(ev.logDateCompleted);
      const isCompleted = ev.logJobStatus === 'Completed' || !!completedTimestamp;
      if (!isCompleted) continue;
      const raw = completedTimestamp || cleanDate(ev.dateEnded) || cleanDate(ev.dateStarted);
      if (!raw) continue;
      const completedDate = parseISO(raw.split(' ')[0]);
      if (isNaN(completedDate.getTime())) continue;
      const daysAgo = differenceInDays(now, completedDate);
      // "completed less than 30 days" from now, excluding future-dated completions
      if (daysAgo < 0 || daysAgo >= 30) continue;
      if (!best || completedDate > best.date) {
        best = { event: ev, date: completedDate, daysAgo };
      }
    }
    if (!best) return null;
    const label = `${
      best.daysAgo === 0 ? 'today' : `${best.daysAgo} day${best.daysAgo === 1 ? '' : 's'} ago`
    } (${format(best.date, 'MMM d')})`;
    return { ...best, label };
  }, [events, enabled]);
};
